import { useEffect, useState } from "react";

import { Button } from "../ui/button.js";
import { Input } from "../ui/input.js";
import { Pane } from "../ui/pane.js";
import { ChapterCard, ChapterSection } from "./chapter.js";

/* ═══════════════════════════════════════════════════════════
   OtelExportSection

   Settings → Agent runs → Telemetry export. Admin-set OTel export:
   point every workspace coding session at the customer's own
   OTLP collector (Datadog, Grafana Cloud, an Agent or Alloy, …)
   for traces, metrics, and log events in Claude Code's shape.

   Security posture, mirrored from the backend accessor:
   - GET /api/settings/otel-export is masked — header NAMES only.
     A saved header value never returns to the browser; existing
     rows render as ●●●●●●●● and an empty value on save means
     "keep what's stored".
   - PUT is admin-gated (manage-otel-export); a member sees the
     config read-only and gets a 403 message if they try to save.
   - Sessions export METADATA ONLY (tokens, cost, tier, tool ids —
     never prompt/response text); that pin is client-enforced and
     not a setting, which is why there is no toggle for it here.
   ═══════════════════════════════════════════════════════════ */

export interface OtelExportSettings {
  enabled: boolean;
  endpoint: string | null;
  protocol: "grpc" | "http/protobuf" | "http/json";
  serviceName: string | null;
  headerNames: string[];
}

export type FetchOtelExport = () => Promise<OtelExportSettings | null>;
export type SaveOtelExport = (patch: Record<string, unknown>) => Promise<OtelExportSettings>;

async function defaultFetchOtelExport(): Promise<OtelExportSettings | null> {
  const res = await fetch("/api/settings/otel-export", { credentials: "include" });
  if (!res.ok) return null;
  return (await res.json()) as OtelExportSettings;
}

async function defaultSaveOtelExport(patch: Record<string, unknown>): Promise<OtelExportSettings> {
  const res = await fetch("/api/settings/otel-export", {
    method: "PUT",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    if (res.status === 403) {
      throw new Error("Only workspace admins can change telemetry export settings.");
    }
    throw new Error(body?.error ?? `Save failed (${res.status}).`);
  }
  return (await res.json()) as OtelExportSettings;
}

const PROTOCOLS: OtelExportSettings["protocol"][] = ["grpc", "http/protobuf", "http/json"];

/** A header row a preset adds on select. A `value` prefills it (non-secret). */
interface PresetHeader {
  name: string;
  valuePlaceholder?: string;
  value?: string;
}

interface VendorPreset {
  label: string;
  protocol: OtelExportSettings["protocol"];
  endpointPlaceholder: string;
  /** When set, the endpoint input is PREFILLED with this exact URL. */
  endpointDefault?: string;
  /** Header rows the vendor needs; added on select when absent. */
  headers?: PresetHeader[];
  help: string;
}

/**
 * Datadog's documented OTLP intake hosts, per site. The endpoint is a BASE:
 * sessions send each signal to its own path under it (`/v1/traces`,
 * `/v1/metrics`, `/v1/logs`), so one host covers traces, metrics, and logs.
 */
const DATADOG_SAAS_SITES: Record<string, string> = {
  us1: "https://otlp.datadoghq.com",
  us3: "https://otlp.us3.datadoghq.com",
  us5: "https://otlp.us5.datadoghq.com",
  eu: "https://otlp.datadoghq.eu",
  ap1: "https://otlp.ap1.datadoghq.com",
};

/** Datadog's routing header for sending OTLP agent traces to LLM Observability. */
const DATADOG_LLMOBS_HEADER: PresetHeader = { name: "dd-otlp-source", value: "llmobs" };

/**
 * Vendor presets carry only what is unambiguous (protocol + the headers the
 * collector requires). Datadog SaaS additionally prefills the exact
 * documented endpoint so there is no endpoint trivia to get right, and adds
 * the header that routes traces into LLM Observability alongside APM; the
 * Agent and Grafana hosts depend on the customer's own deployment, so those
 * stay placeholders. The backend adds the `Basic `/`Bearer ` scheme to a
 * scheme-less `authorization` value, so Grafana users paste the raw
 * `MTgx…` value they were given and we make it valid.
 */
const VENDOR_PRESETS: Record<string, VendorPreset> = {
  custom: {
    label: "Custom collector",
    protocol: "grpc",
    endpointPlaceholder: "http://your-collector.internal:4317",
    help: "Enter any OTLP endpoint, protocol, and headers manually.",
  },
  "datadog-agent": {
    label: "Datadog (Agent)",
    protocol: "grpc",
    endpointPlaceholder: "http://<datadog-agent-host>:4317",
    help: "Point at a Datadog Agent you run with OTLP ingest enabled (off by default: otlp_config.receiver.protocols.grpc.endpoint in datadog.yaml). No key needed — the agent forwards with its own.",
  },
  "datadog-saas": {
    label: "Datadog (direct SaaS)",
    protocol: "http/protobuf",
    endpointPlaceholder: "https://otlp.datadoghq.com",
    endpointDefault: DATADOG_SAAS_SITES.us1!,
    headers: [{ name: "dd-api-key", valuePlaceholder: "your Datadog API key" }, DATADOG_LLMOBS_HEADER],
    help: "Streams straight to Datadog's OTLP intake — no agent needed. Traces land in APM and LLM Observability, metrics and events under yagni_code. The intake accepts http, not gRPC.",
  },
  grafana: {
    label: "Grafana Cloud",
    protocol: "http/protobuf",
    endpointPlaceholder: "https://otlp-gateway-prod-<region>.grafana.net/otlp",
    headers: [{ name: "authorization", valuePlaceholder: "paste the MTgx… value you were given" }],
    help: "Use the base OTLP URL from your stack's OpenTelemetry page (ending in /otlp); paste the raw OTEL_EXPORTER_OTLP_HEADERS value and the Basic scheme is added automatically.",
  },
};

/** The placeholder for a header value input, by header name, for the active preset. */
function presetValuePlaceholder(preset: VendorPreset, headerName: string): string | undefined {
  return preset.headers?.find((h) => h.name === headerName)?.valuePlaceholder;
}

interface HeaderRow {
  /** Stable key for React; the name input is editable only on new rows. */
  key: string;
  name: string;
  value: string;
  /** True when this header already exists server-side (value stays masked). */
  stored: boolean;
  /** Marked for deletion on save (stored rows only). */
  deleted: boolean;
}

export interface OtelExportSectionProps {
  folio?: string;
  fetchSettings?: FetchOtelExport;
  saveSettings?: SaveOtelExport;
}

export function OtelExportSection({
  folio = "03",
  fetchSettings = defaultFetchOtelExport,
  saveSettings = defaultSaveOtelExport,
}: OtelExportSectionProps = {}) {
  // null = loading or read failed → section hidden (same convention as the
  // device-token list above this section).
  const [settings, setSettings] = useState<OtelExportSettings | null>(null);
  const [loaded, setLoaded] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [endpoint, setEndpoint] = useState("");
  const [protocol, setProtocol] = useState<OtelExportSettings["protocol"]>("grpc");
  const [serviceName, setServiceName] = useState("");
  const [rows, setRows] = useState<HeaderRow[]>([]);

  const [preset, setPreset] = useState<string>("custom");
  const [ddSite, setDdSite] = useState<string>("us1");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  function applyServer(next: OtelExportSettings) {
    setSettings(next);
    setEnabled(next.enabled);
    setEndpoint(next.endpoint ?? "");
    setProtocol(next.protocol);
    setServiceName(next.serviceName ?? "");
    setRows(
      next.headerNames.map((name) => ({
        key: `stored:${name}`,
        name,
        value: "",
        stored: true,
        deleted: false,
      })),
    );
  }

  useEffect(() => {
    let cancelled = false;
    fetchSettings()
      .then((loadedSettings) => {
        if (cancelled) return;
        if (loadedSettings) applyServer(loadedSettings);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchSettings]);

  if (!loaded || settings === null) return null;

  function addRow() {
    setRows((current) => [
      ...current,
      { key: `new:${Date.now()}:${current.length}`, name: "", value: "", stored: false, deleted: false },
    ]);
  }

  function applyPreset(key: string) {
    setPreset(key);
    const p = VENDOR_PRESETS[key];
    if (!p) return;
    setProtocol(p.protocol);
    if (p.endpointDefault) {
      setEndpoint((current) => (current.trim() === "" ? p.endpointDefault! : current));
    }
    const presetHeaders = p.headers ?? [];
    if (presetHeaders.length > 0) {
      setRows((current) => {
        const missing = presetHeaders.filter((h) => !current.some((r) => r.name === h.name && !r.deleted));
        if (missing.length === 0) return current;
        return [
          ...current,
          ...missing.map((h, i) => ({
            key: `preset:${h.name}:${Date.now()}:${i}`,
            name: h.name,
            value: h.value ?? "",
            stored: false,
            deleted: false,
          })),
        ];
      });
    }
  }

  /** Site switch fills the endpoint only while it still holds a preset URL. */
  function applyDatadogSite(site: string) {
    setDdSite(site);
    const url = DATADOG_SAAS_SITES[site];
    if (!url) return;
    setEndpoint((current) =>
      Object.values(DATADOG_SAAS_SITES).includes(current.trim()) ? url : current,
    );
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    setSavedAt(null);
    try {
      const headers: Record<string, string | null> = {};
      for (const row of rows) {
        const name = row.name.trim();
        if (!name) continue;
        if (row.deleted) headers[name] = null;
        else if (row.value.trim()) headers[name] = row.value.trim();
        // stored + untouched value → omit: the server keeps what it has.
      }
      const updated = await saveSettings({
        enabled,
        endpoint: endpoint.trim() || null,
        protocol,
        serviceName: serviceName.trim() || null,
        ...(Object.keys(headers).length > 0 ? { headers } : {}),
      });
      applyServer(updated);
      setSavedAt(Date.now());
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <ChapterSection folio={folio} label="Telemetry export" />
      {/* Glass: this form stages an admin decision; the saved state is the
          committed record the backend serves back (masked). */}
      <Pane material="staged" className="p-6" data-testid="otel-export-section">
        <div className="space-y-4">
          <p className="m-0 text-sm text-[var(--text-secondary)]">
            Stream traces, metrics, and log events — tokens, cost, model tier,
            tool calls, lines of code — from every developer's YAGNI Code
            sessions to your own OpenTelemetry collector, in the same shape
            Claude Code emits under a yagni_code prefix. Metadata only: prompt
            and response text never export, and that is enforced by the
            client, not a setting.
          </p>

          <div className="space-y-1">
            <label
              htmlFor="otel-preset"
              className="block text-sm font-medium text-[var(--text-primary)]"
            >
              Collector
            </label>
            <select
              id="otel-preset"
              value={preset}
              onChange={(e) => applyPreset(e.target.value)}
              data-testid="otel-preset"
              className="rounded border border-[var(--print-border)] bg-transparent px-2 py-1.5 text-sm text-[var(--text-primary)]"
            >
              {Object.entries(VENDOR_PRESETS).map(([key, p]) => (
                <option key={key} value={key}>
                  {p.label}
                </option>
              ))}
            </select>
            <p className="m-0 text-xs text-[var(--text-tertiary)]">{VENDOR_PRESETS[preset].help}</p>
            {preset === "datadog-saas" && (
              <div className="space-y-1 pt-1">
                <label
                  htmlFor="otel-dd-site"
                  className="block text-xs font-medium text-[var(--text-primary)]"
                >
                  Datadog site
                </label>
                <select
                  id="otel-dd-site"
                  value={ddSite}
                  onChange={(e) => applyDatadogSite(e.target.value)}
                  data-testid="otel-dd-site"
                  className="rounded border border-[var(--print-border)] bg-transparent px-2 py-1 text-xs text-[var(--text-primary)]"
                >
                  <option value="us1">US1 (datadoghq.com)</option>
                  <option value="us3">US3 (us3.datadoghq.com)</option>
                  <option value="us5">US5 (us5.datadoghq.com)</option>
                  <option value="eu">EU (datadoghq.eu)</option>
                  <option value="ap1">AP1 (ap1.datadoghq.com)</option>
                </select>
                <p className="m-0 text-xs text-[var(--text-tertiary)]">
                  The intake host follows your Datadog site; the endpoint below
                  fills in to match.
                </p>
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm font-medium text-[var(--text-primary)]">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              data-testid="otel-enabled"
            />
            Export telemetry from this workspace's coding sessions
          </label>

          <div className="space-y-1">
            <label
              htmlFor="otel-endpoint"
              className="block text-sm font-medium text-[var(--text-primary)]"
            >
              OTLP endpoint
            </label>
            <Input
              id="otel-endpoint"
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              placeholder={VENDOR_PRESETS[preset].endpointPlaceholder}
              spellCheck={false}
              className="max-w-md font-mono"
            />
            <p className="text-xs text-[var(--text-tertiary)]">
              The base URL of your collector: for http, sessions add
              /v1/traces, /v1/metrics, and /v1/logs themselves; for gRPC the
              host and port alone. Explicit ports only for non-default ones:
              :4317 for gRPC collectors, :4318 for HTTP collectors; https
              endpoints need no port.
            </p>
          </div>

          <div className="flex flex-wrap gap-4">
            <div className="space-y-1">
              <label
                htmlFor="otel-protocol"
                className="block text-sm font-medium text-[var(--text-primary)]"
              >
                Protocol
              </label>
              <select
                id="otel-protocol"
                value={protocol}
                onChange={(e) => setProtocol(e.target.value as OtelExportSettings["protocol"])}
                className="rounded border border-[var(--print-border)] bg-transparent px-2 py-1.5 text-sm text-[var(--text-primary)]"
              >
                {PROTOCOLS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label
                htmlFor="otel-service-name"
                className="block text-sm font-medium text-[var(--text-primary)]"
              >
                Service name <span className="font-normal text-[var(--text-tertiary)]">(optional)</span>
              </label>
              <Input
                id="otel-service-name"
                value={serviceName}
                onChange={(e) => setServiceName(e.target.value)}
                placeholder="yagni-code"
                spellCheck={false}
                className="max-w-xs font-mono"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium text-[var(--text-primary)]">
              Collector headers <span className="font-normal text-[var(--text-tertiary)]">(e.g. dd-api-key)</span>
            </div>
            <p className="m-0 text-xs text-[var(--text-tertiary)]">
              Values are encrypted at rest and never shown again after saving.
              Leave a saved header's value blank to keep it; type a new value to
              rotate it.
            </p>
            {rows.filter((r) => !r.deleted).length === 0 && (
              <p className="m-0 text-xs text-[var(--text-tertiary)]">No headers configured.</p>
            )}
            {rows.map((row, i) =>
              row.deleted ? null : (
                <div key={row.key} className="flex items-center gap-2">
                  <Input
                    value={row.name}
                    onChange={(e) =>
                      setRows((current) =>
                        current.map((r, j) => (j === i ? { ...r, name: e.target.value } : r)),
                      )
                    }
                    placeholder="header-name"
                    disabled={row.stored}
                    spellCheck={false}
                    aria-label={`Header ${i + 1} name`}
                    className="max-w-[14rem] font-mono text-sm"
                  />
                  <Input
                    type="password"
                    value={row.value}
                    onChange={(e) =>
                      setRows((current) =>
                        current.map((r, j) => (j === i ? { ...r, value: e.target.value } : r)),
                      )
                    }
                    placeholder={
                      row.stored
                        ? "●●●●●●●● (saved)"
                        : presetValuePlaceholder(VENDOR_PRESETS[preset]!, row.name) ?? "value"
                    }
                    autoComplete="off"
                    spellCheck={false}
                    aria-label={`Header ${i + 1} value`}
                    className="max-w-[18rem] font-mono text-sm"
                  />
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      setRows((current) =>
                        row.stored
                          ? current.map((r, j) => (j === i ? { ...r, deleted: true } : r))
                          : current.filter((_, j) => j !== i),
                      )
                    }
                  >
                    Remove
                  </Button>
                </div>
              ),
            )}
            <Button size="sm" variant="secondary" onClick={addRow}>
              Add header
            </Button>
          </div>

          <div className="flex items-center gap-3 pt-1">
            <Button onClick={() => void handleSave()} disabled={saving} loading={saving}>
              Save telemetry export
            </Button>
            {saveError ? (
              <span role="alert" className="text-sm text-[var(--error)]">
                {saveError}
              </span>
            ) : savedAt ? (
              <span className="text-sm text-[var(--text-secondary)]">
                Saved. New coding sessions pick this up at launch.
              </span>
            ) : null}
          </div>
        </div>
      </Pane>
      <ChapterCard className="mt-3 p-4">
        <p className="m-0 text-xs text-[var(--text-tertiary)]">
          Sessions fetch this configuration at launch, so changes apply to the
          next session each developer starts. Reported cost uses your
          workspace's contracted rates, and the model name is the tier id —
          the export never identifies the backing model.
        </p>
      </ChapterCard>
    </>
  );
}

export default OtelExportSection;
